// createLaxSegments.ts
import type { Point, PointArray } from '../types/contour-definition-array-type';

import { processPoints } from './contourProcessing';

const EPS = 1e-9;

/** Continuous position (edgeIndex + t) of a point on an OPEN polyline. */
function locateOnPolyline(p: Point, poly: PointArray): number {
  let bestPos = 0;
  let bestDist = Infinity;
  // open: no closing edge from last vertex back to first
  for (let e = 0; e < poly.length - 1; e++) {
    const a = poly[e];
    const b = poly[e + 1];
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const len2 = abx * abx + aby * aby;
    let t = len2 > 0 ? ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const dx = p.x - (a.x + t * abx);
    const dy = p.y - (a.y + t * aby);
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      bestPos = e + t;
    }
  }
  return bestPos;
}

function pointAt(poly: PointArray, pos: number): Point {
  const e = Math.floor(pos);
  const t = pos - e;
  const a = poly[e];
  const b = poly[Math.min(e + 1, poly.length - 1)];
  return { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) };
}

/** Points of an OPEN polyline from position a to b (either direction), endpoints exact. */
function arcPoints(poly: PointArray, a: number, b: number): PointArray {
  const pts: PointArray = [pointAt(poly, a)];
  if (a <= b) {
    for (let k = Math.floor(a) + 1; k < b - EPS; k++) pts.push(poly[k]);
  }
  else {
    for (let k = Math.ceil(a) - 1; k > b + EPS; k--) pts.push(poly[k]);
  }
  pts.push(pointAt(poly, b));
  return pts;
}

/** Reverse a wall + its positions so chord order increases with position. */
function orient(
  poly: PointArray,
  positions: number[],
): { poly: PointArray; positions: number[] } {
  if (positions.length >= 2 && positions[0] > positions[positions.length - 1]) {
    const n = poly.length;
    return {
      poly: [...poly].reverse(),
      positions: positions.map(p => n - 1 - p),
    };
  }
  return { poly, positions };
}

/** Interior points of a (dense) chord, excluding its endpoints (endo -> epi order). */
function chordInterior(chord: PointArray): PointArray {
  return chord.length > 2 ? chord.slice(1, -1) : [];
}

/** Same, reversed (epi -> endo order). */
function reversedChordInterior(chord: PointArray): PointArray {
  return [...chordInterior(chord)].reverse();
}

/**
 * Interior points of the synthetic base connection a -> b (excludes endpoints).
 *
 * Processed as an OPEN path so a and b are preserved exactly: smoothPoints never
 * drops the first/last vertex of an open polyline, and interpolate keeps them on
 * the internal grid. The arcs already supply a and b as the cap's seam vertices,
 * so only the in-between densified points are returned.
 *
 * resolution = 1 -> operate in the internal / non-scaled coordinate space.
 */
function connectorInterior(a: Point, b: Point): PointArray {
  const dense = processPoints(
    [a, b],
    false, // open -> endpoints preserved
    undefined, // width  -> skip clamp
    undefined, // height
    true, // skipInsideCheck
    1, // internal / non-scaled grid
  );
  return dense.length > 2 ? dense.slice(1, -1) : [];
}

/**
 * Build LAX segment polygons from chord lines and the OPEN endo/epi walls.
 *
 * The two end segments are capped by the endo<->epi base connection plus a
 * single chord; interior segments are bounded by two chords.
 * N chords -> N+1 segments.
 *
 * Chords may be dense polylines: only their endpoints (`c[0]` = endo,
 * `c[c.length - 1]` = epi) are used to locate the arc ranges, and their
 * interior points are inserted into the chord seams. The synthetic base
 * connections (not present in chordLines) are densified explicitly.
 *
 * @param chordLines ordered chords (endo -> epi), base->apex->base
 *                   (as produced by createLaxChordLines, possibly densified)
 * @param endoIn     open endocardial polyline (raw coordinates)
 * @param epiIn      open epicardial polyline (raw coordinates)
 */
export function createLaxSegments(
  chordLines: PointArray[],
  endoIn: PointArray,
  epiIn: PointArray,
): PointArray[] {
  const n = chordLines.length;
  if (n < 1 || endoIn.length < 2 || epiIn.length < 2) return [];

  const endoPosRaw = chordLines.map(c => locateOnPolyline(c[0], endoIn));
  const epiPosRaw = chordLines.map(c =>
    locateOnPolyline(c[c.length - 1], epiIn),
  );

  // normalize both walls to the same base->base direction as the chord order
  const { poly: endo, positions: endoPos } = orient(endoIn, endoPosRaw);
  const { poly: epi, positions: epiPos } = orient(epiIn, epiPosRaw);

  const endoEnd = endo.length - 1;
  const epiEnd = epi.length - 1;

  const segments: PointArray[] = [];

  // end cap 1: base .. chord 0
  segments.push([
    ...arcPoints(epi, 0, epiPos[0]),
    ...reversedChordInterior(chordLines[0]),
    ...arcPoints(endo, endoPos[0], 0),
    ...connectorInterior(endo[0], epi[0]),
  ]);

  // interior: chord i .. chord i+1  -> two chords
  for (let i = 0; i < n - 1; i++) {
    segments.push([
      ...arcPoints(epi, epiPos[i], epiPos[i + 1]), // epi_i .. epi_{i+1}
      ...reversedChordInterior(chordLines[i + 1]), // epi_{i+1} -> endo_{i+1}
      ...arcPoints(endo, endoPos[i + 1], endoPos[i]), // endo_{i+1} .. endo_i
      ...chordInterior(chordLines[i]), // endo_i -> epi_i (closing)
    ]);
  }

  // end cap 2: chord n-1 .. base
  segments.push([
    ...arcPoints(epi, epiPos[n - 1], epiEnd),
    ...connectorInterior(epi[epiEnd], endo[endoEnd]),
    ...arcPoints(endo, endoEnd, endoPos[n - 1]),
    ...chordInterior(chordLines[n - 1]),
  ]);

  return segments;
}
